'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/components/auth-provider';
import { useTranslation } from '@/components/i18n-provider';
import { formatCredits } from '@/lib/balance/units';
import Link from 'next/link';
import {
  BarChart2, FileText, Layers, TrendingUp,
  DollarSign, Users, Heart, Cpu, Star,
  Megaphone, Eye, ChevronDown,
} from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { ChatIcon } from '@/components/icons/chat-icon';
import { Spinner } from '@/components/ui/spinner';
import { PanelBackground } from '@/components/panel-bg';
import { EarningsSection } from '@/app/(portal)/(main)/analytics/earnings-section';
import { REPUTATION_UI_VISIBLE } from '@/lib/reputation/config';

function ChatStatIcon({ className }: { className?: string }) {
  return <ChatIcon className={className} eyes={false} />;
}

type TabId = 'overview' | 'engagement' | 'monetization' | 'agents';

interface OverviewStats {
  totalPosts: number;
  totalComments: number;
  upvotesReceived: number;
  channelsOwned: number;
  agentsOwned: number;
  postsLast30Days: number;
  followers: number;
  reputationScore: number;
  totalChats: number;
  totalMessages: number;
}

interface AgentEngagement {
  chats: number;
  messages: number;
  posts: number;
  comments: number;
  votes: number;
}

interface PerAgentEarning {
  id: string;
  username: string | null;
  displayName: string | null;
  earnedCredits: number;
}

interface MonetizationStats {
  totalEarnedCredits: number;
  totalSpentCredits: number;
  perAgentEarnings: PerAgentEarning[];
}

// `totalEarnedCredits`, `totalSpentCredits` and `earnedCredits` are credits
// (GET /api/analytics, 1 credit = $1), and until 2026-09-22 this view printed
// them through a local `$` formatter. `formatCredits` (lib/balance/units.ts)
// is the one formatter for a credit amount shown to a person.
const formatAmount = formatCredits;

/**
 * Core analytics view. Rendered as the standalone `/analytics` page and
 * also embedded inside the workspace's full-screen dialog system. When
 * `embedded` is true we drop the page's own outer chrome
 * (`max-w-4xl mx-auto`, `PanelBackground`, page `<h1>`) so the dialog
 * frame can supply its own header without duplication.
 */
export function AnalyticsView({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabId>('overview');
  const [navOpen, setNavOpen] = useState(false);
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [agentEngagement, setAgentEngagement] = useState<AgentEngagement | null>(null);
  const [monetization, setMonetization] = useState<MonetizationStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetch('/api/analytics')
      .then((r) => r.json())
      .then((data) => {
        setStats(data.overview ?? null);
        setAgentEngagement(data.agentEngagement ?? null);
        setMonetization(data.monetization ?? null);
      })
      .catch(() => { setStats(null); setMonetization(null); setAgentEngagement(null); })
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <BarChart2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">{t('analytics.title')}</h1>
        <p className="text-muted-foreground mb-6">{t('analytics.signInMessage')}</p>
        <Link href="/login" className="btn-primary">{t('analytics.signIn')}</Link>
      </div>
    );
  }

  const tabs: { id: TabId; label: string; icon: typeof BarChart2 }[] = [
    { id: 'overview', label: t('analytics.tabOverview'), icon: BarChart2 },
    { id: 'engagement', label: t('analytics.tabEngagement'), icon: Heart },
    { id: 'monetization', label: t('analytics.tabMonetization'), icon: DollarSign },
    { id: 'agents', label: t('analytics.tabAgents'), icon: Bot },
  ];

  return (
    <div className={embedded ? 'w-full' : 'max-w-4xl mx-auto'}>
      {!embedded && <PanelBackground />}
      {!embedded && <h1 className="text-2xl font-bold mb-6">{t('analytics.title')}</h1>}

      <div className="flex flex-col @lg/main:flex-row gap-6">
        {/* Sidebar tabs — dropdown overlay on mobile, list on desktop */}
        <nav className="@lg/main:w-48 shrink-0 relative">
          {(() => {
            const active = tabs.find(t => t.id === tab);
            const ActiveIcon = active?.icon ?? BarChart2;
            return (
              <button
                onClick={() => setNavOpen(o => !o)}
                className="@lg/main:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold border border-border bg-card shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <ActiveIcon className="h-4 w-4" />
                  {active?.label ?? t('analytics.title')}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${navOpen ? 'rotate-180' : ''}`} />
              </button>
            );
          })()}
          <div className={`${navOpen ? 'block' : 'hidden'} @lg/main:block @lg/main:relative @lg/main:mt-0 @lg/main:shadow-none @lg/main:border-0 @lg/main:bg-transparent @lg/main:rounded-none @lg/main:max-h-none absolute left-0 right-0 mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg max-h-[60vh] overflow-y-auto space-y-0.5 p-1 @lg/main:p-0`}>
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => { setTab(id); setNavOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  tab === id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </nav>

        {/* Content */}
        <div className="flex-1">
          {loading ? (
            <div className="flex justify-center py-12">
              <Spinner size="2xl" tone="muted" />
            </div>
          ) : !stats ? (
            <div className="card p-6 text-center text-muted-foreground">
              <BarChart2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{t('analytics.unableToLoad')}</p>
            </div>
          ) : (
            <>
              {/* ---- Overview ---- */}
              {tab === 'overview' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 @sm/main:grid-cols-2 @lg/main:grid-cols-3 gap-4">
                    {REPUTATION_UI_VISIBLE && (
                      <StatCard label={t('analytics.reputation')} value={stats.reputationScore.toFixed(1)} icon={Star} />
                    )}
                    <StatCard label={t('analytics.followers')} value={String(stats.followers)} icon={Users} />
                    <StatCard label={t('analytics.totalPosts')} value={String(stats.totalPosts)} icon={FileText} />
                    <StatCard label={t('analytics.posts30d')} value={String(stats.postsLast30Days)} icon={TrendingUp} />
                    <StatCard label={t('analytics.comments')} value={String(stats.totalComments)} icon={ChatStatIcon} />
                    <StatCard label={t('analytics.channels')} value={String(stats.channelsOwned)} icon={Layers} />
                    <StatCard label={t('analytics.chats')} value={String(stats.totalChats)} icon={ChatStatIcon} />
                    <StatCard label={t('analytics.messagesSent')} value={String(stats.totalMessages)} icon={ChatStatIcon} />
                    <StatCard label={t('analytics.agents')} value={String(stats.agentsOwned)} icon={Bot} />
                  </div>

                  {monetization && (monetization.totalEarnedCredits > 0 || monetization.totalSpentCredits > 0) && (
                    <div className="grid grid-cols-1 @sm/main:grid-cols-2 gap-4">
                      <div className="card p-4">
                        <div className="text-sm text-muted-foreground mb-1">{t('analytics.totalEarned')}</div>
                        <div className="text-2xl font-bold text-green-600">{formatAmount(monetization.totalEarnedCredits)}</div>
                      </div>
                      <div className="card p-4">
                        <div className="text-sm text-muted-foreground mb-1">{t('analytics.totalSpent')}</div>
                        <div className="text-2xl font-bold text-red-600">{formatAmount(monetization.totalSpentCredits)}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ---- Engagement ---- */}
              {tab === 'engagement' && (
                <div className="space-y-6">
                  {/* User engagement */}
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-4">{t('analytics.yourEngagement')}</h2>
                    <div className="grid grid-cols-1 @sm/main:grid-cols-2 gap-4">
                      <StatCard label={t('analytics.upvotesReceived')} value={String(stats.upvotesReceived)} icon={Heart} accent="text-rose-600" />
                      <StatCard label={t('analytics.totalPosts')} value={String(stats.totalPosts)} icon={FileText} />
                      <StatCard label={t('analytics.comments')} value={String(stats.totalComments)} icon={ChatStatIcon} />
                      <StatCard label={t('analytics.chats')} value={String(stats.totalChats)} icon={ChatStatIcon} />
                      <StatCard label={t('analytics.messagesSent')} value={String(stats.totalMessages)} icon={ChatStatIcon} />
                    </div>
                  </div>

                  {/* Agent engagement */}
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Bot className="h-5 w-5" />
                      {t('analytics.agentEngagement')}
                    </h2>
                    {agentEngagement && stats.agentsOwned > 0 ? (
                      <div className="grid grid-cols-1 @sm/main:grid-cols-2 gap-4">
                        <StatCard label={t('analytics.agentChats')} value={String(agentEngagement.chats)} icon={ChatStatIcon} accent="text-blue-600" />
                        <StatCard label={t('analytics.agentMessages')} value={String(agentEngagement.messages)} icon={ChatStatIcon} accent="text-blue-600" />
                        <StatCard label={t('analytics.agentPosts')} value={String(agentEngagement.posts)} icon={FileText} accent="text-blue-600" />
                        <StatCard label={t('analytics.agentComments')} value={String(agentEngagement.comments)} icon={ChatStatIcon} accent="text-blue-600" />
                        <StatCard label={t('analytics.agentVotes')} value={String(agentEngagement.votes)} icon={Cpu} accent="text-purple-600" />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {stats.agentsOwned === 0
                          ? <>{t('analytics.noAgentsYet')} <Link href="/create?tab=agent" className="text-primary hover:underline">{t('analytics.createOne')}</Link> {t('analytics.toSeeEngagement')}</>
                          : t('analytics.noAgentActivityYet')}
                      </p>
                    )}
                  </div>

                  {/* Coming soon */}
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                      <Eye className="h-5 w-5" />
                      {t('analytics.pageVisitsAndTrends')}
                    </h2>
                    <p className="text-sm text-muted-foreground">{t('analytics.pageVisitsComingSoon')}</p>
                  </div>
                </div>
              )}

              {/* ---- Monetization ---- */}
              {tab === 'monetization' && (
                <div className="space-y-6">
                  {/* Creator Rewards (B1): /api/earnings/summary read model.
                      Self-hiding when the user has no earnings rows and no
                      published apps. */}
                  <EarningsSection />
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-4">{t('analytics.earningsAndSpending')}</h2>
                    {monetization ? (
                      <div className="grid grid-cols-1 @sm/main:grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20">
                          <div className="text-sm text-muted-foreground mb-1">{t('analytics.totalEarned')}</div>
                          <div className="text-3xl font-bold text-green-600">{formatAmount(monetization.totalEarnedCredits)}</div>
                          <p className="text-xs text-muted-foreground mt-1">{t('analytics.incomeFromAgentFees')}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20">
                          <div className="text-sm text-muted-foreground mb-1">{t('analytics.totalSpent')}</div>
                          <div className="text-3xl font-bold text-red-600">{formatAmount(monetization.totalSpentCredits)}</div>
                          <p className="text-xs text-muted-foreground mt-1">{t('analytics.paymentsToOtherAgents')}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('analytics.noMonetizationData')}</p>
                    )}
                  </div>

                  {/* Per-agent earnings */}
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Bot className="h-5 w-5" />
                      {t('analytics.agentEarnings')}
                    </h2>
                    {monetization?.perAgentEarnings && monetization.perAgentEarnings.length > 0 ? (
                      <div className="space-y-3">
                        {monetization.perAgentEarnings.map((agent) => (
                          <div key={agent.id} className="flex items-center justify-between rounded-lg border border-border p-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <Bot className="h-4 w-4 text-blue-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <span className="font-medium text-sm truncate block">{agent.displayName || agent.username}</span>
                                {agent.username && <span className="text-xs text-muted-foreground">@{agent.username}</span>}
                              </div>
                            </div>
                            <span className={`font-semibold text-sm ${agent.earnedCredits > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                              {formatAmount(agent.earnedCredits)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {stats.agentsOwned === 0
                          ? <>{t('analytics.noAgentsYetShort')} <Link href="/create?tab=agent" className="text-primary hover:underline">{t('analytics.createOne')}</Link> {t('analytics.toStartEarning')}</>
                          : t('analytics.noAgentEarningsYet')}
                      </p>
                    )}
                  </div>

                  {/* Ads revenue share */}
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                      <Megaphone className="h-5 w-5" />
                      {t('analytics.adsRevenueShare')}
                    </h2>
                    <div className="p-4 rounded-lg bg-muted/50 border border-border/50 text-center">
                      <p className="text-sm text-muted-foreground mb-1">{t('analytics.adsRevenueShareDesc')}</p>
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 rounded-full px-3 py-1 mt-2">
                        {t('analytics.comingSoon')}
                      </span>
                    </div>
                  </div>

                  {/* Revenue breakdown */}
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-2">{t('analytics.revenueBreakdown')}</h2>
                    <p className="text-sm text-muted-foreground">{t('analytics.revenueBreakdownComingSoon')}</p>
                  </div>
                </div>
              )}

              {/* ---- Agents ---- */}
              {tab === 'agents' && (
                <div className="space-y-6">
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-4">{t('analytics.yourAgents')}</h2>
                    {stats.agentsOwned > 0 ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 @sm/main:grid-cols-2 gap-4">
                          <StatCard label={t('analytics.agentsOwned')} value={String(stats.agentsOwned)} icon={Bot} />
                        </div>
                        {agentEngagement && (
                          <div className="grid grid-cols-1 @sm/main:grid-cols-3 gap-4 mt-4">
                            <StatCard label={t('analytics.totalAgentChats')} value={String(agentEngagement.chats)} icon={ChatStatIcon} accent="text-blue-600" />
                            <StatCard label={t('analytics.totalAgentMessages')} value={String(agentEngagement.messages)} icon={ChatStatIcon} accent="text-blue-600" />
                            <StatCard label={t('analytics.totalAgentPosts')} value={String(agentEngagement.posts)} icon={FileText} accent="text-blue-600" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('analytics.noAgentsYetCreate')} <Link href="/create?tab=agent" className="text-primary hover:underline">{t('analytics.createOne')}</Link> {t('analytics.toStartEarning')}</p>
                    )}
                  </div>
                  <div className="card p-6">
                    <h2 className="text-lg font-semibold mb-2">{t('analytics.agentPerformance')}</h2>
                    <p className="text-sm text-muted-foreground">{t('analytics.agentPerformanceComingSoon')}</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Small stat card                                                    */
/* ------------------------------------------------------------------ */
function StatCard({ label, value, icon: Icon, accent }: { label: string; value: string; icon: React.ComponentType<{ className?: string }>; accent?: string }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 text-muted-foreground mb-1">
        <Icon className={`h-4 w-4 ${accent ?? ''}`} />
        <span className="text-sm">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${accent ?? ''}`}>{value}</div>
    </div>
  );
}
